import Check from "@mui/icons-material/Check";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { Box, Stack, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { WasteManifestSource } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useParams } from "react-router";
import { formatNumber } from "util/formatNumber";

import { WASTE_LINES_UNIFIED_QUERY } from "../ManifestLineItems/api";
import { AmountCell } from "./AmountCell";
import { DiscrepanciesCell } from "./DiscrepanciesCell";

export const LineItemsDataGrid = ({
  processedManifestId,
  onRowClick,
  initialWasteLineNumber,
}: {
  processedManifestId: string | undefined;
  /**
   * Row-click handler. Optional because consumers that open the grid with a
   * specific line already in focus (e.g. the manifest detail drawer) don't
   * need a drill-in target — they leave row clicks as a no-op.
   */
  onRowClick?: (id: string) => void;
  /**
   * Optional initial filter — when the drawer is opened with a specific line
   * in mind (e.g. via a deep link from the waste-stream details modal), the
   * grid starts pre-filtered to that line so the user lands directly on it.
   * Clearing the search reveals the rest of the manifest's lines.
   */
  initialWasteLineNumber?: string;
}) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { processedManifest } = useProcessedManifest(processedManifestId);
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  const showDiscrepancyCounts = isStaff && featureFlags[FeatureFlagKey.WasteManifestDiscrepancies];

  return (
    <Stack spacing={3}>
      {processedManifestId && (
        <OmnisearchDataGrid
          // Scope persistence to this manifest so the search box doesn't carry
          // a stale filter (e.g. an `initialWasteLineNumber` we seeded for a
          // previous open) from one manifest's drawer into another.
          persistenceKey={`manifest-detail-line-items-${processedManifestId}`}
          // White grid surface (so row hover reads clearly); rows are clickable.
          gridSx={{
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
          // White search box (it otherwise shows the pale-gray section card behind it).
          searchFieldSx={{ "& .MuiOutlinedInput-root": { backgroundColor: "background.paper" } }}
          columns={[
            {
              field: "lineItem",
              headerName: "Line Item",
              flex: 2,
              minWidth: 220,
              renderCell: ({ row: { willNotReport }, value }) => {
                return (
                  <Box>
                    <TruncateTypography variant="body2">{value}</TruncateTypography>
                    {willNotReport && (
                      <Typography variant="caption" color="text.secondary">
                        Not Reportable
                      </Typography>
                    )}
                  </Box>
                );
              },
            },
            {
              field: "wasteLineNumber",
              headerName: "Line Item #",
              flex: 1,
              minWidth: 110,
            },
            {
              field: "profileNumber",
              headerName: "Profile #",
              flex: 1,
              minWidth: 110,
            },
            {
              // Collapsed amount: shows the largest of the three source amounts,
              // flagging cross-source disagreement. The per-source columns below
              // stay available (hidden by default) for drill-down/filtering.
              field: "amount",
              headerName: "Amount",
              renderHeader: () => (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  Amount
                  <InfoIconTooltip
                    tooltip={
                      <Typography variant="caption">
                        Displays the highest value across data sources. Mouse over the{" "}
                        <ErrorOutline
                          sx={{
                            fontSize: "0.875rem",
                            color: "warning.main",
                            verticalAlign: "text-bottom",
                          }}
                        />{" "}
                        icon to see amounts from all data sources.
                      </Typography>
                    }
                  />
                </Stack>
              ),
              flex: 1,
              minWidth: 110,
              sortable: false,
              filterable: false,
              renderCell: ({ row }: GridRenderCellParams) => (
                <AmountCell
                  amounts={{
                    [WasteManifestSource.EManifest]: row.eManifestAmount,
                    [WasteManifestSource.Vendor]: row.vendorAmount,
                    [WasteManifestSource.ManifestDocument]: row.documentAmount,
                  }}
                />
              ),
            },
            {
              field: "eManifestAmount",
              headerName: "Amount (e‑Manifest)",
              flex: 1,
              minWidth: 170,
              filterKeyType: "number",
              valueGetter: (_, row) => {
                const amount = row.eManifestAmount;
                return amount ? `${formatNumber(amount)} lbs` : "";
              },
            },
            {
              field: "vendorAmount",
              headerName: "Amount (Vendor)",
              flex: 1,
              minWidth: 150,
              filterKeyType: "number",
              valueGetter: (_, row) => {
                const amount = row.vendorAmount;
                return amount ? `${formatNumber(amount)} lbs` : "";
              },
            },
            {
              field: "documentAmount",
              headerName: "Amount (Document)",
              flex: 1,
              minWidth: 170,
              filterKeyType: "number",
              valueGetter: (_, row) => {
                const amount = row.documentAmount;
                return amount ? `${formatNumber(amount)} lbs` : "";
              },
            },
            {
              field: "managementMethodCode",
              headerName: "Management Method Code",
              flex: 1,
              minWidth: 200,
            },
            {
              field: "federalWasteCodes",
              headerName: "Federal Waste Codes",
              flex: 1,
              minWidth: 170,
              valueGetter: (_, row) => row.federalWasteCodes?.join(", ") ?? "",
            },
            {
              field: "stateWasteCodes",
              headerName: "State Waste Codes",
              flex: 1,
              minWidth: 160,
              valueGetter: (_, row) => row.stateWasteCodes?.join(", ") ?? "",
            },
            ...(showDiscrepancyCounts
              ? [
                  {
                    // Combined unresolved + pending chips, matching the manifest list view.
                    field: "discrepancies" as const,
                    headerName: "Discrepancies",
                    flex: 1,
                    minWidth: 130,
                    sortable: false,
                    filterable: false,
                    renderCell: ({ row }: GridRenderCellParams) => (
                      <DiscrepanciesCell tally={row.discrepancyTally} />
                    ),
                  },
                ]
              : []),
            {
              field: "willNotReport",
              headerName: "Not Reportable",
              filterKeyType: "boolean",
              sortable: false,
              flex: 1,
              minWidth: 140,
              renderCell: ({ value }) => {
                if (value === true) {
                  return <Check />;
                }
                return null;
              },
            },
          ]}
          dataQuery={WASTE_LINES_UNIFIED_QUERY}
          getItems={(data) => data.wasteLinesUnified?.items ?? []}
          getCount={(data) => data.wasteLinesUnified?.count ?? 0}
          withPadding={false}
          rowHeight="auto"
          showManageColumns
          defaultSearch={`tenantId:${tenantId} processedManifestId:${processedManifestId}`}
          // Seeds the visible omnisearch input — unlike `defaultSearch` (hidden
          // filters), `initialFilters` populates the search box so the user can
          // see (and clear) the line filter we narrowed the grid to.
          initialFilters={
            initialWasteLineNumber
              ? [{ field: "wasteLineNumber", value: initialWasteLineNumber }]
              : undefined
          }
          initialSortModel={[{ field: "wasteLineNumber", sort: "asc" }]}
          initialPageSize={5}
          pageSizeOptions={[5, 10]}
          hideFooter={false}
          onRowClick={({ row: { id } }) => onRowClick?.(id)}
          commandButtons={({ search }) => [
            <ExportDataButton
              key="export"
              disabled={!processedManifest}
              fileName={`waste-manifest-${processedManifestId}`}
              exportType="wasteManifestLines"
              tenantId={tenantId}
              filter={{ search }}
              iconOnly
            />,
          ]}
          columnVisibilityModel={{
            willNotReport: false,
            federalWasteCodes: false,
            stateWasteCodes: false,
            // Collapsed into the single "Amount" column; available via manage columns.
            eManifestAmount: false,
            vendorAmount: false,
            documentAmount: false,
          }}
        />
      )}
    </Stack>
  );
};
